/**
 * The choices a dish can offer: a size, extra cheese, no onions.
 *
 * WHY NOTHING HERE EXISTED
 * ------------------------
 * There were no groups, no options, and not one dish offering a single choice
 * — and not because nobody had configured any. The table joining a group to a
 * dish had never held a row, because no endpoint could write one. A group
 * could be created through the API and then had nowhere to go.
 *
 * WHY THE LIMITS ON A GROUP ARE THE POINT OF THIS SCREEN
 * ------------------------------------------------------
 * The pricing code took an option id and added its price with nothing checking
 * the option had anything to do with the dish. An entirely ordinary "Small:
 * −150" from some other dish's size group took Rs. 150 off a Rs. 9,999 pizza,
 * and sending it three times took off Rs. 450 — no approval, no discount
 * limit, and nothing recorded as a discount, because as far as the system knew
 * none had happened.
 *
 * The server enforces all of that now. What this screen decides is what the
 * rules actually are: how many may be chosen, whether one must be.
 *
 * WHY A CHOICE THAT TAKES MONEY OFF IS ALLOWED BUT SHOWN
 * ------------------------------------------------------
 * A smaller portion genuinely costs less, so a negative price is correct here
 * and refusing it would be wrong. But it is also the shape of the mistake that
 * costs money, so it is coloured rather than hidden — somebody setting one up
 * should see it read as money going out.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { describeRules } from './choiceRules.js';

const field =
  'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

export function ChoicesTab({ canEdit }) {
  const [editing, setEditing] = useState(null);
  const queryClient = useQueryClient();

  const groups = useQuery({
    queryKey: ['menu', 'modifier-groups'],
    // Withdrawn ones included, so they can be seen and understood. The till
    // asks without this flag and never sees them.
    queryFn: () => api.get('/menu/modifier-groups?include_inactive=1'),
  });

  const withdraw = useMutation({
    mutationFn: (id) => api.delete(`/menu/modifier-groups/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['menu'] }),
  });

  const rows = groups.data?.groups ?? [];

  return (
    <div>
      {canEdit && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setEditing({})}>Add a choice group</Button>
        </div>
      )}

      {editing && (
        <GroupForm
          group={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['menu'] });
            setEditing(null);
          }}
        />
      )}

      {rows.length === 0 && !groups.isLoading && (
        <p className="mb-4 rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
          {/*
            Named rather than left as an empty table. "No rows" reads as a
            filter problem; this is the actual state of the restaurant.
          */}
          No dish offers any choices yet — no sizes, no extras, no add-ons. Set a group up here,
          then attach it to the dishes that offer it from the Dishes tab.
        </p>
      )}

      <DataTable
        rows={rows}
        loading={groups.isLoading}
        error={groups.isError}
        columns={[
          {
            key: 'name',
            label: 'Group',
            tone: (row) => (row.is_active ? 'text-ink' : 'text-ink-3'),
          },
          {
            key: 'rules',
            label: 'How many',
            render: (row) => describeRules(row),
            tone: () => 'text-ink-2',
          },
          {
            key: 'options',
            label: 'Choices',
            render: (row) =>
              (row.options ?? [])
                .filter((o) => o.is_active)
                .map((o) => o.name)
                .join(', ') || '—',
            tone: () => 'text-ink-2',
          },
          {
            key: 'used_by',
            label: 'On dishes',
            align: 'right',
            // A group attached to nothing is set up and doing nothing, which
            // is the state every group in this system was in.
            render: (row) => (row.is_active ? (row.used_by ?? 0) : '—'),
            tone: (row) => (row.is_active && !row.used_by ? 'text-warn' : 'text-ink-2'),
          },
          {
            key: 'state',
            label: 'In use',
            render: (row) => (row.is_active ? 'Yes' : 'Withdrawn'),
            tone: (row) => (row.is_active ? 'text-ink-2' : 'text-warn'),
          },
          ...(canEdit
            ? [
                {
                  key: 'actions',
                  label: '',
                  align: 'right',
                  render: (row) =>
                    row.is_active ? (
                      <span className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setEditing(row)}
                          className="text-sm text-ink-2 underline hover:text-ink"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={() => withdraw.mutate(row.id)}
                          className="text-sm text-ink-3 underline hover:text-bad"
                        >
                          Withdraw
                        </button>
                      </span>
                    ) : null,
                },
              ]
            : []),
        ]}
      />

      {withdraw.isError && (
        <p className="mt-3 text-sm text-bad">
          {withdraw.error?.message ?? 'That could not be withdrawn.'}
        </p>
      )}
    </div>
  );
}

/**
 * Setting a group up.
 *
 * The saved shape and the edited shape are kept apart, and the edited one is
 * derived rather than copied in when the data lands — setting state during
 * render does not work, because React finishes the current render first and
 * the markup below then reads a value that is not there yet. That is a real
 * crash this codebase has already had once, on the recipe form.
 */
function GroupForm({ group, onClose, onSaved }) {
  const isNew = !group.id;

  const [name, setName] = useState(group.name ?? '');
  const [required, setRequired] = useState(Boolean(group.is_required));
  const [min, setMin] = useState(String(group.min_selections ?? 0));
  const [max, setMax] = useState(String(group.max_selections ?? 1));
  const [options, setOptions] = useState(
    (group.options ?? [])
      .filter((o) => o.is_active)
      .map((o) => ({ id: o.id, name: o.name, priceAdjustment: String(o.price_adjustment ?? 0) }))
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        minSelections: Number(min) || 0,
        maxSelections: Number(max) || 0,
        isRequired: required ? 1 : 0,
        options: options.map((o) => ({
          ...(o.id ? { id: o.id } : {}),
          name: o.name.trim(),
          priceAdjustment: Number(o.priceAdjustment) || 0,
        })),
      };

      return isNew
        ? api.post('/menu/modifier-groups', body)
        : api.put(`/menu/modifier-groups/${group.id}`, body);
    },
    onSuccess: onSaved,
  });

  const floor = required ? Math.max(Number(min) || 0, 1) : Number(min) || 0;

  const usable =
    name.trim() &&
    options.length > 0 &&
    options.every((o) => o.name.trim()) &&
    floor <= options.length &&
    (Number(max) === 0 || floor <= Number(max));

  const change = (index, patch) =>
    setOptions((current) => current.map((o, i) => (i === index ? { ...o, ...patch } : o)));

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-4 font-medium text-ink">
        {isNew ? 'A new choice group' : group.name}
      </h3>

      <div className="mb-4 flex flex-wrap gap-3">
        <label className="min-w-56 flex-1">
          <span className="mb-1.5 block text-xs text-ink-3">What it is called</span>
          <input
            aria-label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Portion size"
            className={field}
          />
        </label>

        <label className="w-32">
          <span className="mb-1.5 block text-xs text-ink-3">At least</span>
          <input
            aria-label="Fewest choices"
            type="number"
            min="0"
            value={min}
            onChange={(e) => setMin(e.target.value)}
            className={`${field} tabular-nums`}
          />
        </label>

        <label className="w-32">
          <span className="mb-1.5 block text-xs text-ink-3">At most</span>
          <input
            aria-label="Most choices"
            type="number"
            min="0"
            value={max}
            onChange={(e) => setMax(e.target.value)}
            className={`${field} tabular-nums`}
          />
        </label>
      </div>

      <label className="mb-4 flex items-center gap-2 text-sm text-ink-2">
        <input
          type="checkbox"
          aria-label="A choice has to be made"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
          className="h-4 w-4"
        />
        A choice has to be made before the dish can be ordered
      </label>

      {/*
        The rules said back in a sentence. Three number boxes do not tell
        anybody what the till will actually do with them.
      */}
      <p className="mb-4 text-sm text-ink-3">
        On the till this reads as: <span className="text-ink-2">{describeRules({
          min_selections: Number(min) || 0,
          max_selections: Number(max) || 0,
          is_required: required ? 1 : 0,
        })}</span>
        {Number(max) === 0 && ' — a maximum of nought means no limit'}
      </p>

      <div className="space-y-2">
        {options.map((option, index) => {
          const price = Number(option.priceAdjustment) || 0;

          return (
            <div key={option.id ?? index} className="flex flex-wrap items-end gap-2">
              <label className="min-w-56 flex-1">
                <span className="sr-only">Choice</span>
                <input
                  aria-label={`Choice ${index + 1}`}
                  value={option.name}
                  onChange={(e) => change(index, { name: e.target.value })}
                  placeholder="Extra cheese"
                  className={field}
                />
              </label>

              <label className="w-36">
                <span className="sr-only">Price change</span>
                <input
                  aria-label={`Choice ${index + 1} price`}
                  type="number"
                  step="1"
                  value={option.priceAdjustment}
                  onChange={(e) => change(index, { priceAdjustment: e.target.value })}
                  className={`${field} tabular-nums`}
                />
              </label>

              <span
                className={`w-28 pb-3 text-right text-sm tabular-nums ${
                  price < 0 ? 'text-warn' : 'text-ink-2'
                }`}
              >
                {/*
                  A negative is legitimate — a smaller portion costs less — and
                  it is also the shape of the mistake that costs money, so it
                  reads as money going out rather than as a number.
                */}
                {price === 0 ? 'No change' : price < 0 ? `−${formatPKR(-price)}` : `+${formatPKR(price)}`}
              </span>

              <button
                type="button"
                onClick={() => setOptions(options.filter((_, i) => i !== index))}
                aria-label={`Remove choice ${index + 1}`}
                className="pb-3 text-sm text-ink-3 hover:text-bad"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <Button
          variant="secondary"
          onClick={() => setOptions([...options, { name: '', priceAdjustment: '0' }])}
        >
          Add a choice
        </Button>
      </div>

      {floor > options.length && (
        <p className="mt-3 text-sm text-warn">
          {/*
            Explained rather than only disabled. This is the setting that makes
            every dish carrying the group impossible to order, and from the
            till it looks like the dish is broken.
          */}
          This asks for {floor} {floor === 1 ? 'choice' : 'choices'} but only has{' '}
          {options.length}. Any dish offering it could not be ordered at all.
        </p>
      )}

      {save.isError && (
        <p className="mt-3 text-sm text-bad">{save.error?.message ?? 'That did not save.'}</p>
      )}

      <div className="mt-5 flex gap-2">
        <Button onClick={() => save.mutate()} disabled={!usable || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
